import { CalendarOutlined } from '@ant-design/icons';
import { ProFormField, ProFormGroup, ProFormSelect } from '@ant-design/pro-form';
import { DatePicker } from '@arco-design/web-react';
import { Input, Space, Tag } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import classnames from 'classnames';
import { range } from 'lodash';
import { isString } from 'lodash-es';
import moment, { Moment } from 'moment';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ReportGuideType } from '@/config/app/auth';
import { getParamFromUrl } from '@/infrastructure/history';
import { ReportScene } from '@/infrastructure/reporter/datong/report';
import {
  CommonDateRange,
  TimeRange,
} from '@/infrastructure/utils/timestamp';
import { TimeRangeFormType } from '@/presentation/components/form';
import styles from '@/presentation/components/form/components/time-range.module.scss';
import { FREE_DATE_QUICK } from '@/presentation/components/form/proto';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';

const { RangePicker } = DatePicker;

const DEFAULT_FORMATTER = 'YYYY/MM/DD';

const DEFAULT_FORMATTER_MINUTE = 'MM/DD HH:mm';

export const DEFAULT_BUGLY_LIMIT_DAYS = 45;

export const DEFAULT_MONITOR_LIMIT_DAYS = 90;

// 关联卡顿列表
export const ANR_LAG_LIMIT_DAYS = 7;

export const DEFAULT_TIME_RANGE_ANALYTICS = [
  moment().subtract(7, 'days')
    .format(DEFAULT_FORMATTER),
  moment().subtract(1, 'days')
    .format(DEFAULT_FORMATTER),
];

// issue默认最近7天，从当前时间开始
export const DEFAULT_TIME_RANGE_ISSUES = [
  moment().subtract(6, 'days')
    .startOf('date')
    .format('YYYY-MM-DD HH:mm:ss'),
  moment().endOf('date')
    .format('YYYY-MM-DD HH:mm:ss'),
];

// 5分钟聚合 当天时间
export const DEFAULT_TIME_RANGE_ISSUES_MINUTE5 = [
  moment()
    .startOf('date')
    .format('YYYY-MM-DD HH:mm:ss'),
  moment().endOf('date')
    .format('YYYY-MM-DD HH:mm:ss'),
];


const formatDateStr = (str: string) => (isString(str) ? moment(str) : str);

const shortRanges: Record<string, any> = {
  ['近1小时']: () => CommonDateRange.hour(),
  ['今天']: () => CommonDateRange.today(),
  ['昨天']: () => CommonDateRange.yesterday(),
  ['近7天']: () => CommonDateRange.lastDaysFull(7),
  ['近30天']: () => CommonDateRange.lastDaysFull(30),
  ['近45天']: () => CommonDateRange.lastDaysFull(45),
  ['近90天']: () => CommonDateRange.lastDaysFull(90),
  ['近180天']: () => CommonDateRange.halfYear(),
  ['custom']: () => CommonDateRange.lastDaysFull(7),
};

export const formatRange = (value?: string[]) => {
  if (!value) return undefined;
  return value?.map(date => formatDateStr(date));
};

export const TimeRangeComponent: FC<{
  className?: string;
  showTime?: boolean;
  limitDays?: number;
  value?: string[];
  onChange?: (value: any) => void;
  scope: string;
  ranges?: Record<string, TimeRange>;
  onPickerChange?: (value: any) => void;
  onShortCutChange?: (value: string) => void;
  open: boolean;
  setOpen: any;
  disabled: boolean;
  quickOptions?: any;
  isFreeProduct?: boolean;
}> = (props) => {
  const { value, onChange, scope, open, setOpen, showTime, limitDays, className, disabled, isFreeProduct } = props;
  const [innerValue, setInnerValue] = useState<any>(formatRange(value));
  const shift = scope === 'analytics' ? 1 : 0;
  const maxRange = scope === 'analytics' ? '近180天' : '近90天';
  const rangeRef = useRef<any>(null);
  const maxRangeValue = scope === 'analytics' ? CommonDateRange.lastDays(180) : CommonDateRange.lastDays(90);
  const defaultRanges: Record<string, TimeRange> = {
    ['今天']: CommonDateRange.today(),
    ['昨天']: CommonDateRange.yesterday(),
    ['近7天']: CommonDateRange.lastDays(7),
    ['近30天']: CommonDateRange.lastDays(30),
    [maxRange]: maxRangeValue,
  };
  const ranges = props.ranges ?? defaultRanges;

  const disabledDate = (current: any) => {
    const endDate = moment().endOf('date')
      .subtract(shift, 'days');
    const endDateWithLimit = moment().endOf('date')
      .subtract(shift, 'days');

    const startWithLimit = moment().startOf('date')
      .subtract(limitDays, 'days');

    return (!!limitDays
      ? (current.isAfter(endDateWithLimit) || current.isBefore(startWithLimit))
      : current.isAfter(endDate));
  };

  const onShortCutClick = (dateStr: string, ranges: [Moment, Moment]) => {
    setInnerValue(ranges);
    onChange?.(ranges);
    setOpen(false);
    props.onShortCutChange?.(dateStr);
  };

  const onDateChange = (_dateString: any, date: any) => {
    const [start, end] = date || [];
    const values = [start?.format('YYYY-MM-DD HH:mm:ss'), end?.format('YYYY-MM-DD HH:mm:ss')];
    onChange?.(values);
    props.onPickerChange?.(values);
  };

  const handleDisabledRangeTime = (dates: any, currentType: string) => {
    // 当前选择的开始日期与结束时间是否为同一天
    if (currentType === 'start' && moment(innerValue[1]).date() === moment(dates).date()) {
      const disabledMin = moment(dates).hours() < moment(innerValue[1]).hours()
        ? []
        : range(moment(innerValue[1]).minutes(), 60);
      return {
        disabledHours: () => range(moment(innerValue[1]).hours() + 1, 24),
        disabledMinutes: () => disabledMin,
      };
    }
    // 当前选择结束日期与开始时间是否为同一天
    if (currentType === 'end' && moment(innerValue[0]).date() === moment(dates).date()) {
      const disabledMin = moment(dates).hours() > moment(innerValue[0]).hours()
        ? []
        : range(moment(innerValue[0]).minutes() + 1);
      return {
        disabledHours: () => range(moment(innerValue[0]).hours()),
        disabledMinutes: () => disabledMin,
      };
    }

    return {};
  };

  useEffect(() => {
    setInnerValue(formatRange(value));
  }, [value]);

  const shortCuts = useMemo(() => {
    const quickOptions = (props.quickOptions || []).filter((item: any) => item.value !== 'custom');

    return quickOptions.map((item: any) => {
      const openGuide = !FREE_DATE_QUICK.includes(item.value);
      return {
        disabled: openGuide && isFreeProduct,
        text: <FreeProductGuide
      openGuide={openGuide}
      guideType={GuideType.POP_CONFIRM}
      display={Display.INLINE}
      reportGuideType={ReportGuideType.Bubble}
      reportScene={ReportScene.DateCascaderQuick}
      reportField={item.label}
      className={isFreeProduct ? 'text-[#8FBCFF]' : undefined}
      >
      {item.label}
  </FreeProductGuide>,
        value: openGuide && isFreeProduct ? undefined : shortRanges?.[item.value],
      };
    });
  }, [JSON.stringify(props.quickOptions)]);

  return (
    <RangePicker
      disabled={disabled}
      className={classnames(className, styles.rangePicker, 'tds-range-picker')}
      // @ts-ignore
      open={open}
      onOpenChange={setOpen}
      onFocus={() => {
        setOpen(true);
      }}
      showTime={showTime}
      disabledTime={handleDisabledRangeTime}
      pickerRef={rangeRef}
      ref={rangeRef}
      disabledDate={disabledDate}
      format={showTime ? DEFAULT_FORMATTER_MINUTE : DEFAULT_FORMATTER}
      allowClear={false}
      dropdownClassName={'reset-date-picker'}
      value={innerValue}
      onChange={onDateChange}
      shortcuts={shortCuts}
      renderExtraFooter={() => (
        <Space>
          {Object.keys(ranges).map((dateStr: string) => <Tag
            color={'blue'}
            style={{ cursor: 'pointer' }}
            data-tid={dateStr}
            onClick={() => {
              onShortCutClick(dateStr, ranges[dateStr]);
            }
            }>{dateStr}</Tag>)}
        </Space>
      )}
    />
  );
};

export const TimeRangeForm: FC<{
  entry: TimeRangeFormType,
  form: FormInstance,
  hidden?: boolean,
  className?: string,
  quickOptions?: any,
  isFreeProduct?: boolean,
}> = (props) => {
  const { name,
    label,
    scope,
    initRange,
    ranges,
    showTime,
    limitDays,
    disabled,
    // dateChange,
    timeTypeOptions = [],
    hiddenType = true } = props.entry;
  const defaultRange = initRange ?? (scope === 'analytics' ? DEFAULT_TIME_RANGE_ANALYTICS : DEFAULT_TIME_RANGE_ISSUES);

  const [open, setOpen] = useState(false);
  const isCompareTimeRange = name.includes('Compare');

  const [pickerHidden, setPickerHidden] = useState(false);
  const [shortCutHidden, setShortCutHidden] = useState(true);

  if (props.hidden) {
    return null;
  }

  return (
    <ProFormField label={label}>
    <div className={styles.timeRangeCascade}>
    <ProFormGroup>
      <ProFormField
        name={name}
        initialValue={isCompareTimeRange ? DEFAULT_TIME_RANGE_ISSUES_MINUTE5 : defaultRange}
        hidden={pickerHidden}
      >
        <TimeRangeComponent
          isFreeProduct={props.isFreeProduct}
          quickOptions={props.quickOptions}
          showTime={showTime}
          limitDays={limitDays}
          scope={scope}
          ranges={ranges}
          onPickerChange={() => {
            props.form.setFieldsValue({
              [isCompareTimeRange ? 'customDateCompare' : 'customDate']: null,
            });
            // dateChange?.(value);
          }}
          onShortCutChange={(value) => {
            props.form.setFieldsValue({
              [isCompareTimeRange ? 'customDateCompare' : 'customDate']: value,
            });
            setShortCutHidden(false);
            setPickerHidden(true);
            setOpen(false);
          }}
          open={open}
          setOpen={setOpen}
          className={props.className}
          disabled={disabled!}
        />
      </ProFormField>
      <ProFormField
        name={isCompareTimeRange ? 'customDateCompare' : 'customDate'}
        hidden={shortCutHidden}
        initialValue={isCompareTimeRange ? getParamFromUrl('customDateCompare') : getParamFromUrl('customDate')}
      >
        <Input
          style={{ width: 238 }}
          onFocus={() => {
            setShortCutHidden(true);
            setPickerHidden(false);
            setOpen(true);
          }}
          suffix={<CalendarOutlined style={{ color: '#707B7C' }} />}
        />
      </ProFormField>
      <ProFormSelect
        options={timeTypeOptions.map(item => ({
          ...item,
          ['data-tid']: `timeType-${item.value}`,
        }))}
        className={classnames({
          [styles.timeType]: true,
        })}
        hidden={hiddenType}
        allowClear={false}
        fieldProps={{
          dropdownClassName: 'reset-font-size',
          value: timeTypeOptions?.[0]?.value,
          showArrow: timeTypeOptions?.length > 1,
        }}
      />
    </ProFormGroup>
    </div>
    </ProFormField>
  );
};
